import { useEffect, useRef, useState } from 'react';
import { X, Volume2, VolumeX, ArrowUpRight } from 'lucide-react';
import { startReelAudio, stopReelAudio } from '../lib/reelAudio';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/jhtdaos4_SMG%20ALL%20LOGO%20PNG.png';

// Scene timeline (ms)
const TOTAL = 22000;
const SCENES = [
  { id: 'leader', start: 0, end: 2200 },
  { id: 'title', start: 2200, end: 5400 },
  { id: 'tagline', start: 5400, end: 8800 },
  { id: 'capabilities', start: 8800, end: 12500 },
  { id: 'brands', start: 12500, end: 16500 },
  { id: 'stats', start: 16500, end: 19400 },
  { id: 'finale', start: 19400, end: TOTAL },
];

const CAP_WORDS = ['AI', 'SI', 'CO-BRAND', 'CULTURE', 'CREATORS', 'CELEBRITY', 'ROI'];

export default function Reel({ open, onClose }) {
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [closing, setClosing] = useState(false);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Drive timeline
  useEffect(() => {
    if (!open) return;
    setClosing(false);
    setElapsed(0);
    startRef.current = performance.now();
    if (!muted) startReelAudio({ duration: TOTAL });
    const tick = () => {
      const t = performance.now() - startRef.current;
      if (t >= TOTAL) {
        setElapsed(TOTAL);
        handleClose();
        return;
      }
      setElapsed(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopReelAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    stopReelAudio();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setTimeout(() => {
      onClose && onClose();
      setClosing(false);
    }, 500);
  };

  const toggleMute = () => {
    if (muted) {
      const remain = Math.max(TOTAL - elapsed, 1000);
      startReelAudio({ duration: remain });
      setMuted(false);
    } else {
      stopReelAudio();
      setMuted(true);
    }
  };

  if (!open) return null;

  const active = SCENES.find((s) => elapsed >= s.start && elapsed < s.end) || SCENES[SCENES.length - 1];
  // local progress within current scene (0..1)
  const sceneP = Math.min(Math.max((elapsed - active.start) / (active.end - active.start), 0), 1);
  const globalP = elapsed / TOTAL;

  // Format timecode
  const ms = Math.floor(elapsed);
  const ff = Math.floor((ms / (1000 / 24)) % 24).toString().padStart(2, '0');
  const ss = Math.floor((ms / 1000) % 60).toString().padStart(2, '0');
  const tc = `00:00:${ss}:${ff}`;

  return (
    <div
      className={`fixed inset-0 z-[300] bg-black flex items-center justify-center transition-all duration-500 ${
        closing ? 'opacity-0 scale-[1.03]' : 'opacity-100 scale-100'
      }`}
    >
      {/* Letterbox bars */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-[8vh] bg-black z-30" />
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-[8vh] bg-black z-30" />

      {/* Scanlines + grain + vignette */}
      <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay"
           style={{
             backgroundImage:
               "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence baseFrequency='0.95' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
           }}
      />
      <div className="pointer-events-none absolute inset-0 opacity-50 bg-[linear-gradient(transparent_0px,transparent_3px,rgba(255,255,255,0.025)_3px,rgba(255,255,255,0.025)_4px)] bg-[length:100%_4px]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.85)_100%)]" />

      {/* HUD top */}
      <div className="absolute top-[calc(8vh+12px)] left-6 right-6 md:left-10 md:right-10 z-40 flex items-center justify-between text-[10px] md:text-[11px] tracking-[0.3em] text-white/70">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#FF3D2E] animate-pulse" />
          PLAYING <span className="text-white/30">/</span> SMG REEL · 2026
        </div>
        <div className="hidden md:flex items-center gap-6">
          <span className="text-white/40">SCENE {SCENES.indexOf(active) + 1}/{SCENES.length}</span>
          <span className="text-white/40 uppercase">{active.id}</span>
          <span className="font-mono tabular-nums text-white/85">{tc}</span>
        </div>
      </div>

      {/* HUD bottom */}
      <div className="absolute bottom-[calc(8vh+12px)] left-6 right-6 md:left-10 md:right-10 z-40">
        <div className="flex items-center justify-between text-[10px] md:text-[11px] tracking-[0.3em] text-white/70 mb-3">
          <span className="font-mono tabular-nums text-white/85">{tc}</span>
          <span className="hidden md:inline text-white/40">REEL 01 — TAKE 07</span>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              aria-label="toggle sound"
              className="w-9 h-9 border border-white/25 flex items-center justify-center text-white/75 hover:text-white hover:border-white/60 transition-colors"
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={handleClose}
              className="inline-flex items-center gap-2 px-4 py-2 border border-white/25 text-white/80 hover:text-white hover:border-white/60 transition-colors"
            >
              CLOSE <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="relative h-px w-full bg-white/10 overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[#FF3D2E]" style={{ width: `${globalP * 100}%` }} />
        </div>
      </div>

      {/* ---- SCENES ---- */}
      <div className="relative z-10 w-full h-full flex items-center justify-center px-6">
        {active.id === 'leader' && <SceneLeader p={sceneP} />}
        {active.id === 'title' && <SceneTitle p={sceneP} />}
        {active.id === 'tagline' && <SceneTagline p={sceneP} />}
        {active.id === 'capabilities' && <SceneCapabilities p={sceneP} elapsed={elapsed} />}
        {active.id === 'brands' && <SceneBrands p={sceneP} />}
        {active.id === 'stats' && <SceneStats p={sceneP} />}
        {active.id === 'finale' && <SceneFinale p={sceneP} onClose={handleClose} />}
      </div>
    </div>
  );
}

// ---------- Scenes ----------

function SceneLeader({ p }) {
  // Show countdown 3, 2, 1
  const num = p < 0.33 ? 3 : p < 0.66 ? 2 : 1;
  return (
    <div className="relative flex flex-col items-center">
      {/* Circular sweep */}
      <svg viewBox="0 0 200 200" className="w-72 h-72 md:w-96 md:h-96">
        <circle cx="100" cy="100" r="92" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <line x1="100" y1="8" x2="100" y2="192" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <line x1="8" y1="100" x2="192" y2="100" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <circle
          cx="100"
          cy="100"
          r="92"
          fill="none"
          stroke="#FF3D2E"
          strokeWidth="2"
          strokeDasharray={`${578 * ((p % 0.333) / 0.333)} 578`}
          transform="rotate(-90 100 100)"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-white text-[10rem] md:text-[14rem] leading-none">
        {num}
      </span>
      <p className="absolute -bottom-2 text-[10px] tracking-[0.45em] text-white/40">FILM LEADER</p>
    </div>
  );
}

function SceneTitle({ p }) {
  const fade = Math.min(p * 3, 1);
  const scale = 0.92 + fade * 0.08;
  return (
    <div
      className="flex flex-col items-center"
      style={{ opacity: fade, transform: `scale(${scale})`, transition: 'transform 200ms' }}
    >
      <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-white/55 mb-6 flex items-center gap-3">
        <span className="w-8 h-px bg-[#FF3D2E]" />
        PRESENTING
        <span className="w-8 h-px bg-[#FF3D2E]" />
      </p>
      <div className="relative">
        <span
          className="absolute inset-0 -m-12 rounded-full blur-3xl opacity-70"
          style={{ background: 'radial-gradient(closest-side, rgba(255,61,46,0.4), rgba(120,30,160,0.18) 50%, transparent 75%)' }}
        />
        <img src={LOGO_URL} alt="SMG" className="relative w-72 md:w-[420px]" draggable={false} />
      </div>
    </div>
  );
}

function SceneTagline({ p }) {
  const fade = Math.min(p * 2.5, 1);
  return (
    <div className="text-center" style={{ opacity: fade }}>
      <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-white/55 mb-6">— A CREATIVE STUDIO —</p>
      <h2 className="font-display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
        BUILT FOR
      </h2>
      <h2 className="font-script italic text-[#FF3D2E] text-6xl md:text-8xl lg:text-9xl leading-[0.95] mt-2">
        disruption.
      </h2>
      <p className="mt-8 text-white/55 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
        Engineering brands at the intersection of AI, synthetic intelligence, and culture.
      </p>
    </div>
  );
}

function SceneCapabilities({ elapsed }) {
  // Each word visible ~500ms, cycling through
  const wordIndex = Math.floor((elapsed / 500) % CAP_WORDS.length);
  const word = CAP_WORDS[wordIndex];
  return (
    <div className="text-center w-full">
      <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-white/55 mb-8">CAPABILITIES</p>
      <div
        key={word}
        className="font-display text-white text-7xl md:text-[10rem] lg:text-[12rem] leading-[0.9] tracking-tight"
        style={{ animation: 'fadeUp 0.25s ease-out' }}
      >
        {word}
      </div>
      <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
        {CAP_WORDS.map((w, i) => (
          <span
            key={w}
            className={`text-[10px] tracking-[0.3em] px-3 py-1.5 border transition-colors ${
              i === wordIndex
                ? 'border-[#FF3D2E] text-white bg-[#FF3D2E]/10'
                : 'border-white/15 text-white/50'
            }`}
          >
            {w}
          </span>
        ))}
      </div>
    </div>
  );
}

function SceneBrands({ p }) {
  const brands = ['nike', 'mercedes', 'delta', 'jones', 'comisario'];
  // Reveal 0..N over the scene
  const visible = Math.min(Math.ceil(p * brands.length), brands.length);
  return (
    <div className="w-full flex flex-col items-center">
      <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-white/55 mb-10">SELECTED CLIENTS</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 w-full max-w-5xl">
        {brands.map((b, i) => (
          <div
            key={b}
            className={`bg-[#F4EFE4] h-24 md:h-32 flex items-center justify-center transition-all duration-500 ${
              i < visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-95'
            }`}
          >
            <BrandMark k={b} />
          </div>
        ))}
      </div>
      <p className="mt-8 text-[10px] tracking-[0.4em] text-white/40">
        + A CONFIDENTIAL ROSTER OF FOUNDERS, ATHLETES &amp; TALENT
      </p>
    </div>
  );
}

function BrandMark({ k }) {
  const cls = 'flex items-center justify-center text-black w-full h-full px-4';
  // Real brand assets (same as Selected Clients grid)
  const REAL_LOGOS = {
    nike: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/6708jfdd_Nike-Logo.png',
    comisario: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/bhqjatih_client-comisario-removebg-preview.png',
  };
  if (REAL_LOGOS[k]) {
    return (
      <div className={cls + ' py-2'}>
        <img
          src={REAL_LOGOS[k]}
          alt={k}
          className="h-10 md:h-14 w-auto max-w-[80%] object-contain"
          draggable={false}
        />
      </div>
    );
  }
  switch (k) {
    case 'mercedes':
      return (
        <div className={cls}>
          <svg viewBox="0 0 200 50" className="w-2/3 max-w-[220px]">
            <circle cx="25" cy="25" r="14" fill="none" stroke="#000" strokeWidth="2" />
            <path d="M25 11 L25 25 M25 25 L13 33 M25 25 L37 33" stroke="#000" strokeWidth="2" />
            <text x="50" y="32" fontFamily="Georgia, serif" fontSize="20" fill="#000">Mercedes-Benz</text>
          </svg>
        </div>
      );
    case 'delta':
      return (
        <div className={cls}>
          <svg viewBox="0 0 200 50" className="w-2/3 max-w-[220px]">
            <polygon points="10,42 30,10 50,42" fill="#000" />
            <polygon points="20,42 30,25 40,42" fill="#F4EFE4" />
            <text x="62" y="37" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="26" letterSpacing="3" fill="#000">DELTA</text>
          </svg>
        </div>
      );
    case 'jones':
      return (
        <div className={cls + ' flex-col'}>
          <p className="font-display text-2xl tracking-[0.15em]">JONES</p>
          <p className="font-display text-[9px] tracking-[0.4em]">SODA CO.</p>
        </div>
      );
    default:
      return null;
  }
}

function SceneStats({ p }) {
  // Reveal each stat sequentially
  const showA = p > 0.05;
  const showB = p > 0.35;
  const showC = p > 0.65;
  return (
    <div className="w-full text-center">
      <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-white/55 mb-12">BY THE NUMBERS</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto">
        <Stat show={showA} value="320+" label="CREATOR & CELEBRITY NETWORK" />
        <Stat show={showB} value="$340M" label="CO-BRAND REVENUE DRIVEN" />
        <Stat show={showC} value="4.7x" label="AVG. ROAS ON CAMPAIGNS" />
      </div>
    </div>
  );
}

function Stat({ show, value, label }) {
  return (
    <div
      className="flex flex-col items-center"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 500ms, transform 500ms',
      }}
    >
      <div className="font-display text-white text-6xl md:text-8xl tracking-tight">{value}</div>
      <p className="mt-3 text-[10px] tracking-[0.3em] text-white/55 max-w-[220px]">{label}</p>
    </div>
  );
}

function SceneFinale({ p, onClose }) {
  const fade = Math.min(p * 2, 1);
  return (
    <div className="flex flex-col items-center text-center" style={{ opacity: fade }}>
      <div className="relative">
        <span
          className="absolute inset-0 -m-16 rounded-full blur-3xl opacity-70"
          style={{ background: 'radial-gradient(closest-side, rgba(255,61,46,0.45), rgba(120,30,160,0.2) 50%, transparent 75%)' }}
        />
        <img src={LOGO_URL} alt="SMG" className="relative w-60 md:w-96" draggable={false} />
      </div>
      <h3 className="mt-8 font-display text-white text-3xl md:text-5xl tracking-tight">
        Let's build something <span className="font-script text-[#FF3D2E] italic">culture-defining.</span>
      </h3>
      <a
        href="/#contact"
        onClick={() => onClose && onClose()}
        className="mt-10 group inline-flex items-center gap-2 px-7 py-4 bg-[#FF3D2E] hover:bg-[#ff5446] text-white text-[11px] tracking-[0.3em] transition-colors"
      >
        START A PROJECT
        <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
      </a>
    </div>
  );
}
